import { createContext, useContext, useState, useEffect } from 'react';
import { apiUrl } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load auth from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('readwell_token');
    const storedUser = localStorage.getItem('readwell_user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));

      fetch(apiUrl('/api/auth/validate'), {
        headers: {
          Authorization: `Bearer ${storedToken}`,
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error('Invalid token');
          }
          const data = await response.json();
          if (!data.valid) {
            throw new Error('Token not valid');
          }
        })
        .catch(() => {
          setToken(null);
          setUser(null);
          localStorage.removeItem('readwell_token');
          localStorage.removeItem('readwell_user');
        });
    }

    setLoading(false);
  }, []);

  const loginStudent = async (accessCode) => {
    const response = await fetch(apiUrl('/api/auth/student/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessCode })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();

    // Store in state and localStorage
    setToken(data.token);
    setUser({
      type: 'student',
      accessCode: data.accessCode,
      districtId: data.districtId
    });

    localStorage.setItem('readwell_token', data.token);
    localStorage.setItem('readwell_user', JSON.stringify({
      type: 'student',
      accessCode: data.accessCode,
      districtId: data.districtId
    }));

    return data;
  };

  const loginTeacher = async (email, password) => {
    const response = await fetch(apiUrl('/api/auth/teacher/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();

    // Store in state and localStorage
    setToken(data.token);
    setUser(data.user);

    localStorage.setItem('readwell_token', data.token);
    localStorage.setItem('readwell_user', JSON.stringify(data.user));

    return data;
  };

  const setTeacherFromOAuth = async (session) => {
    // Store OAuth teacher session from Better Auth
    const teacherUser = {
      type: 'teacher',
      id: session.user.id,
      email: session.user.email,
      name: session.user.name,
      image: session.user.image,
      role: session.user.role || 'teacher',
      schoolId: session.user.schoolId,
      districtId: session.user.districtId,
    };

    setUser(teacherUser);
    setToken(session.session.token);
    localStorage.setItem('readwell_token', session.session.token);
    localStorage.setItem('readwell_user', JSON.stringify(teacherUser));
    return teacherUser;
  };

  const logout = async () => {
    try {
      if (token) {
        // Try our custom auth logout first
        await fetch(apiUrl('/api/auth/logout'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Also try Better Auth logout for OAuth users
        if (user?.type === 'teacher' && user?.id) {
          await fetch(apiUrl('/api/auth/sso/sign-out'), {
            method: 'POST',
            credentials: 'include',
          });
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state and localStorage regardless of API call success
      setToken(null);
      setUser(null);
      localStorage.removeItem('readwell_token');
      localStorage.removeItem('readwell_user');
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    isStudent: user?.type === 'student',
    isTeacher: user?.type === 'teacher',
    loginStudent,
    loginTeacher,
    setTeacherFromOAuth,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
